import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DeleteTechnicianModal from '../DeleteTechnicianModal/DeleteTechnicianModal';
import { useDeleteTechnicianModalStore } from '../DeleteTechnicianModal/DeleteTechnicianModalStore';
import { useAddTechnicianToObjectModalStore } from '../AddTechnicanToObjectModal/AddTechnicianToObjectModalStore';
import PopupTechListItem from '../components/PopupTechList';
import AddTechnicianModalToObject from '../AddTechnicanToObjectModal/AddTechnicianToObjectModal';

import { useManageObjectModalV2Store } from './ManageObjectModalV2Store';

import CreateSiteContactModal from 'pages/ClientDashboard/Modals/CreateSiteContantModal';
import { siteFormSchema, SiteFormValues } from 'api/clients/types';
import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import GreenAddButton from 'components/Button/GreenAddButton';
import useGetObject from 'hooks/Object/useGetObject';
import useUpdateObject from 'hooks/Object/useUpdateObject';
import useGetClientsOnMap from 'hooks/Clients/useGetClientsOnMap';
import useGetAvailableManagers from 'hooks/Object/useGetAvailableManager';
import useCreateSite from 'hooks/Object/useCreateSite';
import CreateEmployeeModal from 'pages/ClientDashboard/Modals/CreateEmployeeModal';
import CreateTechnicianModal from 'pages/ClientDashboard/Modals/CreateTechnicianModal';
import { useGetAvailableTechnicians } from 'hooks/Object/useGetAvailableTechnicians';
import { countries } from 'api/clients/country-list';

interface ModalProps {
  handleZoomToMarker?: (position: [number, number]) => void;
}

const objectDefaultValues: SiteFormValues = {
  name: '',
  city: '',
  street: '',
  building_number: '',
  full_name: '',
  apartment_no: '',
  manager: 0,
  company: 0,
  technicians: [],
  country: '',
  zip_code: '',
};

const ManageObjectModalV2: React.FC<ModalProps> = ({ handleZoomToMarker }) => {
  const { t } = useTranslation();
  const { isDeleteOnObjectModal } = useDeleteTechnicianModalStore();
  const {
    isOpen,
    id: objectId,
    company, // from store
    currentTab,
    onManageObjectModalV2Close,
  } = useManageObjectModalV2Store();

  const { data, refetch: refetchObject } = useGetObject(objectId);

  const { updateObject } = useUpdateObject();
  const { refetch: refetchMap } = useGetClientsOnMap();
  const { createSite } = useCreateSite();
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isAddSiteContactOpen, setIsAddSiteContactOpen] = useState(false);

  const { data: availableManagers, refetch: refetchAvailableManagers } = useGetAvailableManagers();
  const { data: availableTechnicians, refetch: refetchAvailableTechnicians } = useGetAvailableTechnicians();
  const { open: openAddTechnicianToObjectModalStore } = useAddTechnicianToObjectModalStore();
  const [isAddTechnicianToObjectModal, setIsAddTechnicianToObjectModal] = useState<boolean>(false);

  const [isAddTechnicianToNewSite, setIsAddTechnicianToNewSite] = useState<boolean>(false);

  const { handleSubmit, control, reset, setError } = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: objectDefaultValues,
  });

  const onReset = () => {
    reset(objectDefaultValues);
  };

  useEffect(() => {
    let countryId = '';
    if (data?.country) {
      const foundCountry = countries.find(c => c.countryName === data.country);
      if (foundCountry) {
        countryId = String(foundCountry.id);
      }
    }
    if (objectId) {
      reset({
        name: data?.name ?? '',
        full_name: data?.full_name ?? data?.name ?? '',
        city: data?.city ?? '',
        street: data?.street ?? '',
        building_number: data?.building_number ?? '',
        apartment_no: data?.apartment_no ?? '',
        company: data?.company ?? company,
        manager: data?.manager ?? undefined,
        technicians: (data?.technicians ?? [])
          .map(tech => tech?.technician?.id)
          .filter((id): id is number => typeof id === 'number'),
        country: countryId || '',
        zip_code: data?.zip_code ?? '',
      });
    } else {
      reset({
        ...objectDefaultValues,
        company: company,

        technicians: [],
      });
    }
  }, [objectId, data, reset, company]);
  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: t('Modals.dont-have-country') }];
    }
    return [{ value: '', label: t('Modals.country-select') }].concat(
      countries.map(country => {
        return {
          value: String(country.id),
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, [t]);
  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );
  const onCloseAll = () => {
    onManageObjectModalV2Close();
    onReset();
  };

  const onCreateNewObject = (form: SiteFormValues) => {
    const companyId = company;

    if (!companyId) {
      enqueueSnackbar('Company is missing', { variant: 'error' });
      return;
    }

    const payload: SiteFormValues = {
      ...form,
      full_name: form.name,
      company: companyId as any,
      technicians: form.technicians ?? [],
      country: form.country ? countryList.find(c => c.value === form.country)?.label || '' : '',
    };

    createSite(payload, {
      onSuccess: (response: any) => {
        enqueueSnackbar(t('Modals.success-create', { objectName: response.name }), { variant: 'success' });

        onCloseAll();
        refetchMap();

        if (
          handleZoomToMarker &&
          response?.position &&
          Array.isArray(response.position) &&
          response.position.length === 2
        ) {
          handleZoomToMarker(response.position as [number, number]);
        }
      },
      onError: (error: any) => {
        if (error?.response?.data?.name) {
          setError('name', { message: t('Modals.manage-object.nameAlreadyExists') });
        }
      },
    });
  };
  const managerOptions = useMemo(() => {
    if (!availableManagers || availableManagers.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noManagersAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectManager') }].concat(
      availableManagers.map(manager => ({
        value: String(manager.id),
        label: manager.first_name && manager.last_name ? `${manager.first_name} ${manager.last_name}` : manager.email,
      })),
    );
  }, [availableManagers, t]);

  const technicianOptions = useMemo(() => {
    if (!availableTechnicians || availableTechnicians.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noTechniciansAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectTechnician') }].concat(
      availableTechnicians.map(tp => ({
        value: String(tp.id),
        label: tp.label || tp.email,
      })),
    );
  }, [availableTechnicians, t]);
  const onUpdateObject = (form: SiteFormValues) => {
    const payload: SiteFormValues = {
      ...form,
      full_name: form.name,
      company: (company ?? form.company ?? null) as any,
      technicians: form.technicians ?? [],
      country: form.country ? countryList.find(c => c.value === form.country)?.label || '' : '',
    };

    updateObject(
      { object: payload, id: objectId },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-create', { objectName: data?.name }), { variant: 'success' });
          onCloseAll();
          refetchMap();
        },
      },
    );
  };
  const onFormError = (errors: any) => {
    if (errors.technicians) {
      enqueueSnackbar(t('Modals.manage-object.noTechWarning', 'Please select a technician!'), {
        variant: 'error',
      });
    }
  };
  // Only show this tab like before
  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} toggle={onCloseAll} size="lg">
        <ModalHeader className="border-bottom py-3 " toggle={onCloseAll}>
          <div className="text-purple">
            {objectId ? t('Modals.manage-object.edit.title') : `${t('Modals.manage-object.create.title')}`}
          </div>
        </ModalHeader>

        {currentTab === 'clientCreation' && (
          <>
            <ModalBody>
              <div className="d-flex justify-content-center">
                <form className="w-100" onSubmit={e => e.preventDefault()}>
                  {/* Name */}
                  <div className="row">
                    <div className="mb-3 col-md-12 col-sm-12">
                      <Controller
                        control={control}
                        name="name"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            type="text"
                            label={t('Modals.manage-client.short-name')}
                            error={error?.message}
                            required
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div className="row">
                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="country"
                        render={({ field, fieldState: { error } }) => (
                          <MLSelect
                            {...field}
                            label={t('Modals.manage-client.country')}
                            error={error?.message}
                            options={countryList}
                            formatOptionLabel={formatCountryOption}
                            value={String(field.value || '')}
                            onChange={val => field.onChange(val || '')}
                            required
                          />
                        )}
                      />
                    </div>
                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="city"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            type="text"
                            label={t('Modals.manage-client.city')}
                            error={error?.message}
                            required
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="zip_code"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            type="text"
                            label={t('Modals.manage-client.zip')}
                            error={error?.message}
                            required
                            {...field}
                            value={field.value ?? ''}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="row">
                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="street"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            type="text"
                            error={error?.message}
                            label={t('Modals.manage-client.street')}
                            required
                            {...field}
                          />
                        )}
                      />
                    </div>

                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="building_number"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            error={error?.message}
                            type="text"
                            label={t('Modals.manage-client.building_no')}
                            {...field}
                          />
                        )}
                      />
                    </div>

                    <div className="mb-3 col-md-4">
                      <Controller
                        control={control}
                        name="apartment_no"
                        render={({ field, fieldState: { error } }) => (
                          <MLInput
                            type="text"
                            error={error?.message}
                            label={t('Modals.manage-client.apartment_no')}
                            {...field}
                          />
                        )}
                      />
                    </div>
                  </div>

                  {/* Persons */}
                  <div className="row gap-2">
                    <h4 className="my-3 border-bottom pb-2">{t('Modals.manage-object.persons')}</h4>
                    {/* Manager */}
                    <div className="d-flex gap-2 justify-content-center align-items-center">
                      <div className="mb-4 col-md-11">
                        <Controller
                          control={control}
                          name="manager"
                          render={({ field, fieldState: { error } }) => (
                            <MLSelect
                              required
                              label={t('Modals.manage-technician.manager')}
                              error={error?.message}
                              options={managerOptions}
                              value={field.value != null ? String(field.value) : ''}
                              onChange={val => field.onChange(val ? Number(val) : null)}
                            />
                          )}
                        />
                      </div>

                      <GreenAddButton
                        title={t('Modals.manage-client.createNewOwner')}
                        onClick={() => setIsEmployeeModalOpen(true)}
                      />
                    </div>
                  </div>

                  <div>
                    {objectId && (
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="title">{t('Modals.manage-object.techniques')}</div>
                        <GreenAddButton
                          title={t('Modals.manage-technician.technician')}
                          onClick={() => {
                            if (!data?.slug) return;
                            openAddTechnicianToObjectModalStore(data.slug);
                            setIsAddTechnicianToObjectModal(true);
                          }}
                        />
                      </div>
                    )}
                    {!objectId && (
                      <>
                        <div className="d-flex gap-2 justify-content-center align-items-center">
                          <div className="mb-4 col-md-11">
                            <Controller
                              control={control}
                              name="technicians"
                              render={({ field, fieldState: { error } }) => (
                                <MLSelect
                                  label={t('Modals.manage-technician.technician')}
                                  error={error?.message}
                                  options={technicianOptions}
                                  value={field.value?.[0] != null ? String(field.value[0]) : ''}
                                  onChange={val => field.onChange(val ? [Number(val)] : [])}
                                />
                              )}
                            />
                          </div>

                          <div className="col-md-1 flex-shrink-1">
                            <GreenAddButton
                              title={t('Modals.manage-object.createAndSelectTechnician')}
                              onClick={() => setIsAddTechnicianToNewSite(true)}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {objectId &&
                      data?.technicians?.map(tech => (
                        <PopupTechListItem
                          canDelete={data?.technicians?.length > 1}
                          key={tech?.technician?.id}
                          siteTechnician={tech}
                          refetchObject={refetchObject}
                          objectSlug={data.slug}
                        />
                      ))}
                  </div>
                </form>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button className="btn btn-ghost-danger" onClick={onCloseAll}>
                {t('Modals.delete-modal.cancel')}
              </Button>

              <Button
                className="btn btn-ghost-success"
                onClick={
                  objectId ? handleSubmit(onUpdateObject, onFormError) : handleSubmit(onCreateNewObject, onFormError)
                }
              >
                {objectId ? t('Modals.delete-modal.edit') : t('Modals.delete-modal.create')}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
      <CreateSiteContactModal
        isOpen={isAddSiteContactOpen}
        onClose={() => {
          setIsAddSiteContactOpen(false);
          refetchObject();
        }}
      />
      <CreateEmployeeModal
        isAddingEmployee
        isOpen={isEmployeeModalOpen}
        onClose={() => {
          setIsEmployeeModalOpen(false);
          refetchAvailableManagers();
        }}
      />
      <CreateTechnicianModal
        isClientModal={false}
        isOpen={isAddTechnicianToNewSite}
        onClose={() => {
          setIsAddTechnicianToNewSite(false);
          refetchObject();
          refetchAvailableTechnicians();
        }}
      />{' '}
      <AddTechnicianModalToObject
        close={() => {
          setIsAddTechnicianToObjectModal(false);
          refetchObject();
        }}
        isOpen={isAddTechnicianToObjectModal}
      />
      {isDeleteOnObjectModal && <DeleteTechnicianModal refetch={refetchObject} />}
    </>
  );
};

export default ManageObjectModalV2;
